import {
	registerSeoScripts,
	resetScriptRegistry,
	registerSeoMetaTags,
	registerCanonicalLink,
} from './seoScriptRegistry'

// Define script rendering strategies
type ScriptStrategy = 'server' | 'afterInteractive' | 'lazyOnload' | 'worker'

// Define meta tag interface for og tags and other meta tags
interface MetaTag {
	id?: string
	property?: string
	name?: string
	content: string
}

// Define the script item interface to match the one in seoScriptRegistry
interface ScriptItem {
	id: string
	content?: string
	src?: string
	type?: string
	async?: boolean
	defer?: boolean
	position?: 'head' | 'body'
	strategy?: ScriptStrategy
}

// Enhanced OpenGraph interface with more fields
export interface OpenGraphData {
	// Basic OG properties
	locale?: string
	title?: string
	description?: string
	url?: string
	image?: string
	imageSecureUrl?: string
	type?: string
	siteName?: string

	// Article specific properties
	articlePublishedTime?: string
	articleModifiedTime?: string
	articlePublisher?: string
	articleAuthor?: string
	articleSection?: string
	articleTags?: string[]

	// Twitter specific properties
	twitterCard?: string
	twitterSite?: string
	twitterTitle?: string
	twitterDescription?: string
	twitterCreator?: string
	twitterImage?: string

	// Canonical URL
	canonicalUrl?: string
}

// Helper to generate all meta tags (OG, Twitter, Article) in batches
export const generateOpenGraphTags = (data: OpenGraphData): MetaTag[] => {
	const tags: MetaTag[] = []
	const id = 'generated'

	// Basic OG tags
	if (data.locale) {
		tags.push({
			id: `${id}-og-locale`,
			property: 'og:locale',
			content: data.locale,
		})
	}

	if (data.title) {
		tags.push({
			id: `${id}-og-title`,
			property: 'og:title',
			content: data.title,
		})
	}

	if (data.description) {
		tags.push({
			id: `${id}-og-description`,
			property: 'og:description',
			content: data.description,
		})
	}

	if (data.image) {
		tags.push({
			id: `${id}-og-image`,
			property: 'og:image',
			content: data.image,
		})

		if (data.imageSecureUrl) {
			tags.push({
				id: `${id}-og-image-secure-url`,
				property: 'og:image:secure_url',
				content: data.imageSecureUrl,
			})
		} else if (data.image.startsWith('https://')) {
			tags.push({
				id: `${id}-og-image-secure-url`,
				property: 'og:image:secure_url',
				content: data.image,
			})
		}
	}

	if (data.url) {
		tags.push({
			id: `${id}-og-url`,
			property: 'og:url',
			content: data.url,
		})
	}

	if (data.type) {
		tags.push({
			id: `${id}-og-type`,
			property: 'og:type',
			content: data.type,
		})
	} else {
		tags.push({
			id: `${id}-og-type`,
			property: 'og:type',
			content: 'website',
		})
	}

	if (data.siteName) {
		tags.push({
			id: `${id}-og-site-name`,
			property: 'og:site_name',
			content: data.siteName,
		})
	}

	// Article specific tags
	if (
		data.type === 'article' ||
		data.articlePublishedTime ||
		data.articleModifiedTime ||
		data.articlePublisher
	) {
		if (data.articlePublishedTime) {
			tags.push({
				id: `${id}-article-published-time`,
				property: 'article:published_time',
				content: data.articlePublishedTime,
			})
		}

		if (data.articleModifiedTime) {
			tags.push({
				id: `${id}-article-modified-time`,
				property: 'article:modified_time',
				content: data.articleModifiedTime,
			})
		}

		if (data.articlePublisher) {
			tags.push({
				id: `${id}-article-publisher`,
				property: 'article:publisher',
				content: data.articlePublisher,
			})
		}

		if (data.articleAuthor) {
			tags.push({
				id: `${id}-article-author`,
				property: 'article:author',
				content: data.articleAuthor,
			})
		}

		if (data.articleSection) {
			tags.push({
				id: `${id}-article-section`,
				property: 'article:section',
				content: data.articleSection,
			})
		}

		if (data.articleTags && data.articleTags.length > 0) {
			data.articleTags.forEach((tag, index) => {
				tags.push({
					id: `${id}-article-tag-${index}`,
					property: 'article:tag',
					content: tag,
				})
			})
		}
	}

	// Twitter Card tags
	if (data.twitterCard) {
		tags.push({
			id: `${id}-twitter-card`,
			name: 'twitter:card',
			content: data.twitterCard,
		})
	}

	if (data.twitterSite) {
		tags.push({
			id: `${id}-twitter-site`,
			name: 'twitter:site',
			content: data.twitterSite,
		})
	}

	if (data.twitterTitle) {
		tags.push({
			id: `${id}-twitter-title`,
			name: 'twitter:title',
			content: data.twitterTitle,
		})
	}

	if (data.twitterDescription) {
		tags.push({
			id: `${id}-twitter-description`,
			name: 'twitter:description',
			content: data.twitterDescription,
		})
	}

	if (data.twitterCreator) {
		tags.push({
			id: `${id}-twitter-creator`,
			name: 'twitter:creator',
			content: data.twitterCreator,
		})
	}

	if (data.twitterImage) {
		tags.push({
			id: `${id}-twitter-image`,
			name: 'twitter:image',
			content: data.twitterImage || data.image || '',
		})
	}

	return tags
}

// Register OpenGraph tags directly
export const registerOpenGraph = (data: OpenGraphData): void => {
  const tags = generateOpenGraphTags(data);
  // Filter out tags without an id or ensure each tag has an id
  const validTags = tags.map(tag => tag.id ? tag : { ...tag, id: `og-${Math.random().toString(36).substr(2, 9)}` });
  registerSeoMetaTags(validTags as any);
};

// Helper to get nested property value from object using dot notation
const getNestedValue = (obj: any, path: string): any => {
  if (!obj || !path) return undefined;

  // Handle simple property access first
  if (!path.includes('.') && obj[path] !== undefined) {
    return obj[path];
  }

  // Handle nested property access (e.g., "procedure.title")
  const parts = path.split('.');
  let current = obj;

  for (const part of parts) {
    if (current === null || current === undefined) {
      return undefined;
    }
    current = current[part];
  }

  return current;
};

const normalizeSeoUrl = (url: string): string => {
  try {
    const parsedUrl = new URL(url);

    if (parsedUrl.pathname !== '/') {
      parsedUrl.pathname = parsedUrl.pathname.replace(/\/+$/, '');
    }

    return parsedUrl.toString();
  } catch {
    const normalizedUrl = url.replace(/\/+$/, '');

    return normalizedUrl || url;
  }
};

const normalizeSeoDate = (date: unknown, fallbackDate: Date): string => {
  if (date) {
    const parsedDate = new Date(String(date));

    if (!Number.isNaN(parsedDate.getTime())) {
      return parsedDate.toISOString();
    }
  }

  return fallbackDate.toISOString();
};

const DEFAULT_PUBLIC_SITE_URL = 'https://www.harmonface.com';
const LEGACY_DIRECTUS_URL = 'https://harmon.wtgdigital.io';
const DEFAULT_TWITTER_IMAGE =
	'https://www.harmonface.com/api/assets/421394a6-37d4-4e14-841a-fd0ac9d509ad/bleph-choose-dr-mob.webp';

const getPublicSiteUrl = (): string => {
	const baseUrl = (process.env.NEXT_PUBLIC_BASE_URL || DEFAULT_PUBLIC_SITE_URL).replace(/\/+$/, '');

	try {
		const parsedUrl = new URL(baseUrl);
		const isLocalhost =
			parsedUrl.hostname === 'localhost' || parsedUrl.hostname === '127.0.0.1';
		const isWhiteLabelDomain = parsedUrl.hostname.endsWith('wtgdigital.io');

		return isLocalhost || isWhiteLabelDomain ? DEFAULT_PUBLIC_SITE_URL : baseUrl;
	} catch {
		return DEFAULT_PUBLIC_SITE_URL;
	}
};

const getDirectusOrigin = (): string => {
	try {
		return new URL(process.env.NEXT_PUBLIC_DIRECTUS_URL || LEGACY_DIRECTUS_URL).origin;
	} catch {
		return LEGACY_DIRECTUS_URL;
	}
};

const getPublicAssetUrl = (assetPath: string): string =>
	`${getPublicSiteUrl()}/api/assets/${assetPath.replace(/^\/+/, '').replace(/^assets\/+/, '')}`;

const normalizePublicAssetUrl = (url: string | undefined): string | undefined => {
	if (!url) return undefined;

	if (url.startsWith('/api/assets/')) {
		return `${getPublicSiteUrl()}${url}`;
	}

	if (url.startsWith('/assets/')) {
		return getPublicAssetUrl(url);
	}

	try {
		const parsedUrl = new URL(url);
		const directusOrigin = getDirectusOrigin();
		const publicSiteOrigin = getPublicSiteUrl();
		const isDirectusAsset =
			parsedUrl.pathname.startsWith('/assets/') &&
			(parsedUrl.origin === directusOrigin || parsedUrl.origin === LEGACY_DIRECTUS_URL);
		const isSiteAsset =
			parsedUrl.pathname.startsWith('/assets/') &&
			parsedUrl.origin === publicSiteOrigin;
		const isSiteProxyAsset =
			parsedUrl.pathname.startsWith('/api/assets/') &&
			parsedUrl.origin === publicSiteOrigin;

		if (isDirectusAsset || isSiteAsset) {
			return `${getPublicSiteUrl()}/api${parsedUrl.pathname}${parsedUrl.search}`;
		}

		if (isSiteProxyAsset) {
			return `${getPublicSiteUrl()}${parsedUrl.pathname}${parsedUrl.search}`;
		}
	} catch {
		return url;
	}

	return url;
};

const getDirectusAssetUrl = (image: any): string | undefined => {
	if (!image) return undefined;

	if (typeof image === 'string') {
		return normalizePublicAssetUrl(image);
	}

	const imageData = image.image_file?.image || image.image || image;
	const imageId = imageData?.id;

	if (!imageId) return undefined;

	const filename = imageData.filename_disk || imageData.filename_download || '';

	return filename
		? getPublicAssetUrl(`/assets/${imageId}/${filename}`)
		: getPublicAssetUrl(`/assets/${imageId}`);
};

const getFirstComponentRecord = (componentGroup: any): any => {
	if (!componentGroup || typeof componentGroup !== 'object') return undefined;

	return componentGroup.root_data || Object.values(componentGroup).find(Boolean);
};

const getPageSocialImage = (pageData: any): string | undefined => {
	const blogPostImage = getDirectusAssetUrl(pageData?.featured_image);

	if (blogPostImage) return blogPostImage;

	const firstPostImage = Array.isArray(pageData?.posts)
		? pageData.posts.map((post: any) => getDirectusAssetUrl(post?.featured_image)).find(Boolean)
		: undefined;

	if (firstPostImage) return firstPostImage;

	const heroData = getFirstComponentRecord(pageData?.componentData?.cmp_hero_main);
	const heroImage =
		getDirectusAssetUrl(heroData?.image_mobile) ||
		getDirectusAssetUrl(heroData?.image_desktop);

	return heroImage;
};

// Create flattened placeholder map for common patterns
const createPlaceholderMap = (pageData: any): Record<string, string> => {
  const map: Record<string, string> = {};

  if (!pageData) return map;

  const fallbackDate = new Date();
  const createdDate = normalizeSeoDate(
    pageData.date_created || pageData.published_at || pageData.created_at,
    fallbackDate
  );
  const updatedFallbackDate = new Date(createdDate);
  const updatedDate = normalizeSeoDate(
    pageData.date_updated || pageData.modified_date || pageData.updated_at || createdDate,
    updatedFallbackDate
  );

  // Add direct properties
  if (pageData.title) map.title = pageData.title;
  if (pageData.slug) map.slug = pageData.slug;
  map.date_created = createdDate;
  map.date_updated = updatedDate;
  map.modified_date = updatedDate;
  map.published_at = createdDate;
  if (pageData.procedureTitle) map.procedureTitle = String(pageData.procedureTitle);
  if (pageData.patientNumber !== undefined && pageData.patientNumber !== null) {
    map.patientNumber = String(pageData.patientNumber);
  }
  // Canonical path: root page is "/" even when Directus slug is "home".
  map.site_path = pageData.slug === 'home' ? '/' : '/' + (pageData.slug || '');

  // Gallery procedure placeholders
  if (pageData.procedure) {
    map.procedureTitle = map.procedureTitle || pageData.procedure.title || '';
    map.procedureSlug = pageData.procedure.slug || '';
    map['procedure.title'] = pageData.procedure.title || '';
    map['procedure.slug'] = pageData.procedure.slug || '';
  }

  // Gallery patient placeholders
  if (pageData.patient) {
    const patientId = pageData.patient.id !== undefined && pageData.patient.id !== null
      ? String(pageData.patient.id)
      : '';

    map.patientTitle = pageData.patient.title || '';
    map.patientNumber = map.patientNumber || patientId;
    map.patientId = patientId;
    map['patient.title'] = pageData.patient.title || '';
    map['patient.id'] = patientId;
  }

  // Blog category placeholders
  if (pageData.category) {
    map.categoryTitle = pageData.category.title || '';
    map.categorySlug = pageData.category.slug || '';
    map['category.title'] = pageData.category.title || '';
    map['category.slug'] = pageData.category.slug || '';
  }

  // Blog tag placeholders
  if (pageData.tag) {
    map.tagTitle = pageData.tag.title || '';
    map.tagSlug = pageData.tag.slug || '';
    map['tag.title'] = pageData.tag.title || '';
    map['tag.slug'] = pageData.tag.slug || '';
  }

  // Blog author placeholders
  if (pageData.author) {
    map.authorTitle = pageData.author.title || '';
    map.authorSlug = pageData.author.slug || '';
    map.authorName = pageData.author.title || pageData.author.name || '';
    map['author.title'] = pageData.author.title || '';
    map['author.slug'] = pageData.author.slug || '';
  }

  // Archive placeholders
  if (pageData.archiveSlug) {
    map.archiveSlug = pageData.archiveSlug;
    // Parse archive date for display
    const [year, month] = pageData.archiveSlug.split('-');
    if (year && month) {
      const date = new Date(parseInt(year), parseInt(month) - 1);
      map.archiveDate = date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
    }
  }

  // Search query placeholder
  if (pageData.searchQuery) {
    map.searchQuery = pageData.searchQuery;
  }

  return map;
};

const getSeoEntryValue = (seoData: any[], types: string[]): string | undefined => {
  for (const seoItem of seoData) {
    if (!seoItem.seo_collection_id?.entries || !Array.isArray(seoItem.seo_collection_id?.entries)) {
      continue;
    }

    for (const entry of seoItem.seo_collection_id.entries) {
      if (entry.seo_entries_id && types.includes(entry.seo_entries_id.type)) {
        return entry.seo_entries_id.entry;
      }
    }
  }

  return undefined;
};

const replacePagePlaceholders = (
  text: string,
  pageData: any,
  placeholderMap: Record<string, string>
): string => {
  return text.replace(/{([^{}]+)}/g, (match, variable) => {
    if (placeholderMap[variable] !== undefined) {
      return placeholderMap[variable];
    }

    const nestedValue = getNestedValue(pageData, variable);
    if (nestedValue !== undefined) {
      return String(nestedValue);
    }

    if (pageData[variable] !== undefined) {
      return String(pageData[variable]);
    }

    return match;
  });
};

// Function to replace template variables in OpenGraph data
export const processTemplateVariables = (ogData: OpenGraphData, pageData: any, seoData: any[] = []): OpenGraphData => {
  if (!pageData) return ogData;

  // Create a deep copy to avoid modifying the original
  const processedData = JSON.parse(JSON.stringify(ogData));

  // Create placeholder map for quick lookups
  const placeholderMap = createPlaceholderMap(pageData);

  // Process each field in the OpenGraph data
  Object.entries(processedData).forEach(([key, value]) => {
    if (typeof value === 'string') {
      // Replace template variables like {title} with values from pageData
      processedData[key as keyof OpenGraphData] = value.replace(
        /{([^{}]+)}/g,
        (match, variable) => {
          // Check if it's a SEO type variable (e.g., {seo.description})
          if (variable.startsWith('seo.')) {
            const seoType = variable.substring(4); // Extract the type (e.g., "description")

            // Look for a SEO entry with the specified type
            for (const seoItem of seoData) {
              if (!seoItem.seo_collection_id?.entries || !Array.isArray(seoItem.seo_collection_id?.entries)) {
                continue;
              }

              // Search through all entries
              for (const entry of seoItem.seo_collection_id.entries) {
                if (entry.seo_entries_id && entry.seo_entries_id.type === seoType) {
                  const seoEntry = entry.seo_entries_id.entry;

                  return seoEntry
                    ? replacePagePlaceholders(seoEntry, pageData, placeholderMap)
                    : match;
                }
              }
            }

            // If no matching SEO entry is found, return the original match
            return match;
          }

          if (variable === 'title' && placeholderMap.title === undefined) {
            const seoTitle = getSeoEntryValue(seoData, ['title', 'page_title']);

            if (seoTitle && seoTitle !== match) {
              const resolvedSeoTitle = replacePagePlaceholders(seoTitle, pageData, placeholderMap);

              if (resolvedSeoTitle !== match) {
                return resolvedSeoTitle;
              }
            }
          }

          // Check placeholder map first (handles common patterns like procedureTitle)
          return replacePagePlaceholders(match, pageData, placeholderMap);
        }
      );
    }
  });

  return processedData;
};

// Export for use in _app.tsx
export { createPlaceholderMap };

export const processSeoDataForRegistry = (
	seoData: any,
	pageData: any = null,
	isStaging: boolean = false
) => {
	// Reset registry to ensure no scripts from previous requests remain
	resetScriptRegistry()

	if (!seoData || !Array.isArray(seoData) || seoData.length === 0) {
		return
	}

	const scripts: ScriptItem[] = []
	const metaTags: MetaTag[] = []
	let title = ''
	let ogData: OpenGraphData = {}

	// Process each SEO collection
	seoData.forEach((seoItem, indexMain) => {
		if (
			!seoItem.seo_collection_id?.entries ||
			!Array.isArray(seoItem.seo_collection_id?.entries)
		) {
			return
		}

		seoItem.seo_collection_id.entries.forEach((entry, index) => {
			if (!entry.seo_entries_id) return

			const scriptId = `${indexMain}-${index}`

			// Process schema scripts - these should be server-side for SEO
			if (entry.seo_entries_id.type === 'schema') {
				scripts.push({
					id: scriptId,
					type: 'application/ld+json',
					content: entry.seo_entries_id.entry || '',
					position: 'head',
					strategy: 'server', // Explicitly server-side
				})
			}

			// Store the title (we don't register it as a script, but save for reference)
			if (entry.seo_entries_id.type === 'title' || entry.seo_entries_id.type === 'page_title') {
				title = entry.seo_entries_id.entry || ''
				ogData.title = title // Use title for OG data as well
			}

			if (entry.seo_entries_id.type === 'description') {
				ogData.description =
					entry.seo_entries_id.entry || ogData.description || ''
			}

			// Process Open Graph batch
			if (entry.seo_entries_id.type === 'og') {
				try {
					const ogBatch = JSON.parse(entry.seo_entries_id.entry || '{}')

					// Merge with existing OG data
					ogData = {
						...ogData,
						...ogBatch,
						// Map specific fields if they exist in the batch
						locale: ogBatch.locale || ogData.locale,
						title: ogBatch.title || ogData.title,
						description: ogBatch.description || ogData.description,
						url: ogBatch.url || ogData.url,
						image: ogBatch.image || ogData.image,
						imageSecureUrl:
							ogBatch.image_secure_url ||
							ogBatch.imageSecureUrl ||
							ogData.imageSecureUrl,
						type: ogBatch.type || ogData.type,
						siteName: ogBatch.site_name || ogBatch.siteName || ogData.siteName,
					}
				} catch (e) {
					console.error('Error parsing OG batch data:', e)
				}
			}

			// Process Article batch
			if (entry.seo_entries_id.type === 'article') {
				try {
					const articleBatch = JSON.parse(entry.seo_entries_id.entry || '{}')

					// Merge with existing OG data
					ogData = {
						...ogData,
						...articleBatch,
						// Map specific fields if they exist in the batch
						articlePublishedTime:
							articleBatch.published_time ||
							articleBatch.articlePublishedTime ||
							ogData.articlePublishedTime,
						articleModifiedTime:
							articleBatch.modified_time ||
							articleBatch.articleModifiedTime ||
							ogData.articleModifiedTime,
						articlePublisher:
							articleBatch.publisher ||
							articleBatch.articlePublisher ||
							ogData.articlePublisher,
						articleAuthor:
							articleBatch.author ||
							articleBatch.articleAuthor ||
							ogData.articleAuthor,
						articleSection:
							articleBatch.section ||
							articleBatch.articleSection ||
							ogData.articleSection,
						articleTags:
							articleBatch.tags ||
							articleBatch.articleTags ||
							ogData.articleTags,
					}
				} catch (e) {
					console.error('Error parsing Article batch data:', e)
				}
			}

			// Process Twitter batch
			if (entry.seo_entries_id.type === 'twitter') {
				try {
					const twitterBatch = JSON.parse(entry.seo_entries_id.entry || '{}')

					// Merge with existing OG data
					ogData = {
						...ogData,
						...twitterBatch,
						// Map specific fields if they exist in the batch
						twitterCard:
							twitterBatch.card ||
							twitterBatch.twitterCard ||
							ogData.twitterCard,
						twitterSite:
							twitterBatch.site ||
							twitterBatch.twitterSite ||
							ogData.twitterSite,
						twitterTitle:
							twitterBatch.title ||
							twitterBatch.twitterTitle ||
							ogData.twitterTitle,
						twitterDescription:
							twitterBatch.description ||
							twitterBatch.twitterDescription ||
							ogData.twitterDescription,
						twitterCreator:
							twitterBatch.creator ||
							twitterBatch.twitterCreator ||
							ogData.twitterCreator,
						twitterImage:
							twitterBatch.image ||
							twitterBatch.twitterImage ||
							ogData.twitterImage,
					}
				} catch (e) {
					console.error('Error parsing Twitter batch data:', e)
				}
			}

			// Process individual OG tags (for backward compatibility)
			if (entry.seo_entries_id.type === 'og:title') {
				ogData.title = entry.seo_entries_id.entry || ogData.title || ''
			}

			if (entry.seo_entries_id.type === 'og:description') {
				ogData.description =
					entry.seo_entries_id.entry || ogData.description || ''
			}

			if (entry.seo_entries_id.type === 'og:image') {
				ogData.image = entry.seo_entries_id.entry || ogData.image || ''
			}

			if (entry.seo_entries_id.type === 'og:image:secure_url') {
				ogData.imageSecureUrl =
					entry.seo_entries_id.entry || ogData.imageSecureUrl || ''
			}

			if (entry.seo_entries_id.type === 'og:url') {
				ogData.url = entry.seo_entries_id.entry || ogData.url || ''
			}

			if (entry.seo_entries_id.type === 'og:type') {
				ogData.type = entry.seo_entries_id.entry || ogData.type || 'website'
			}

			if (entry.seo_entries_id.type === 'og:site_name') {
				ogData.siteName = entry.seo_entries_id.entry || ogData.siteName || ''
			}

			// Process individual Twitter tags (for backward compatibility)
			if (entry.seo_entries_id.type === 'twitter:card') {
				ogData.twitterCard =
					entry.seo_entries_id.entry ||
					ogData.twitterCard ||
					'summary_large_image'
			}

			if (entry.seo_entries_id.type === 'twitter:site') {
				ogData.twitterSite =
					entry.seo_entries_id.entry || ogData.twitterSite || ''
			}

			if (entry.seo_entries_id.type === 'twitter:creator') {
				ogData.twitterCreator =
					entry.seo_entries_id.entry || ogData.twitterCreator || ''
			}

			if (entry.seo_entries_id.type === 'twitter:title') {
				ogData.twitterTitle =
					entry.seo_entries_id.entry || ogData.twitterTitle || ''
			}

			if (entry.seo_entries_id.type === 'twitter:description') {
				ogData.twitterDescription =
					entry.seo_entries_id.entry || ogData.twitterDescription || ''
			}

			if (entry.seo_entries_id.type === 'twitter:image') {
				ogData.twitterImage =
					entry.seo_entries_id.entry || ogData.twitterImage || ''
			}

			// Google Tag Manager - server-side for better performance
			if (entry.seo_entries_id.type === 'google-tag-manager') {
				scripts.push({
					id: `${scriptId}-gtm`,
					content: `
            (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
            new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
            j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
            'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
            })(window,document,'script','dataLayer','${entry.seo_entries_id.entry}');
          `,
					position: 'head',
					strategy: 'server',
				})
			}

			// Google Analytics - can be client-side with afterInteractive strategy
			if (entry.seo_entries_id.type === 'google-tag') {
				// GA script source - client-side loading with afterInteractive
				scripts.push({
					id: `${scriptId}-1`,
					src: `https://www.googletagmanager.com/gtag/js?id=${entry.seo_entries_id.entry}`,
					async: true,
					strategy: 'server', // Client-side strategy
				})

				// GA initialization script
				scripts.push({
					id: `${scriptId}-2`,
					content: `
            window.dataLayer = window.dataLayer || [];
            function gtag(){dataLayer.push(arguments);}
            gtag('js', new Date());
            gtag('config','${entry.seo_entries_id.entry}');
          `,
					strategy: 'server', // Client-side strategy
				})
			}

			// WhatConverts - only load in production environments
			if (entry.seo_entries_id.type === 'whatconverts') {
				// Check if we're in production environment
				const isProduction = process.env.NODE_ENV === 'production' && process.env.ENVIRONMENT !== 'development'
				
				if (isProduction) {
					scripts.push({
						id: `${scriptId}-2`,
						content: `
            var $wc_load=function(a){return JSON.parse(JSON.stringify(a))},$wc_leads=$wc_leads||{doc:{url:$wc_load(document.URL),ref:$wc_load(document.referrer),search:$wc_load(location.search),hash:$wc_load(location.hash)}};
          `,
						strategy: 'server', // Client-side with lazy loading
					})

					scripts.push({
						id: `${scriptId}-1`,
						src: entry.seo_entries_id.entry,
						async: true,
						strategy: 'server', // Client-side with lazy loading
					})
				}
			}

			// Process canonical URL
			if (entry.seo_entries_id.type === 'canonical') {
				ogData.canonicalUrl = entry.seo_entries_id.entry || ''
			}
		})
	})

	if (title) {
		ogData.title = isStaging ? '(Staging Version) ' + title : title
	}

	// Process template variables in the collected OG data
	if (Object.keys(ogData).length > 0 && pageData) {
		ogData = processTemplateVariables(ogData, pageData, seoData)
	}

	if (ogData.url) {
		ogData.url = normalizeSeoUrl(ogData.url)
	}

	if (ogData.canonicalUrl) {
		ogData.canonicalUrl = normalizeSeoUrl(ogData.canonicalUrl)
	}

	ogData.image = normalizePublicAssetUrl(ogData.image)
	ogData.imageSecureUrl = normalizePublicAssetUrl(ogData.imageSecureUrl)
	ogData.twitterImage = normalizePublicAssetUrl(ogData.twitterImage)

	const pageSocialImage = pageData ? getPageSocialImage(pageData) : undefined
	ogData.image =
		ogData.image && ogData.image !== DEFAULT_TWITTER_IMAGE
			? ogData.image
			: pageSocialImage || DEFAULT_TWITTER_IMAGE
	ogData.imageSecureUrl =
		ogData.imageSecureUrl && ogData.imageSecureUrl !== DEFAULT_TWITTER_IMAGE
			? ogData.imageSecureUrl
			: ogData.image
	ogData.twitterCard = ogData.twitterCard || 'summary_large_image'
	ogData.twitterTitle = ogData.twitterTitle || ogData.title
	ogData.twitterDescription = ogData.twitterDescription || ogData.description
	ogData.twitterImage =
		ogData.twitterImage && ogData.twitterImage !== DEFAULT_TWITTER_IMAGE
			? ogData.twitterImage
			: ogData.image

	// Generate meta tags from collected OG data
	if (Object.keys(ogData).length > 0) {
		const ogTags = generateOpenGraphTags(ogData)
		metaTags.push(...ogTags)

		// Register canonical URL if available
		if (ogData.canonicalUrl) {
			registerCanonicalLink({
				id: 'canonical-link',
				href: ogData.canonicalUrl,
			})
		}
	}

	// Register the collected scripts with our registry
	registerSeoScripts(scripts)

	// Register meta tags with our registry
	if (metaTags.length > 0) {
		// Filter out tags without an id or ensure each tag has an id
		const validMetaTags = metaTags.map(tag => {
			const tagWithId = tag.id
				? tag
				: { ...tag, id: `meta-${Math.random().toString(36).substr(2, 9)}` }

			if (!pageData || typeof tagWithId.content !== 'string') {
				return tagWithId
			}

			const processedContent = processTemplateVariables(
				{ description: tagWithId.content },
				pageData,
				seoData
			).description
			const content = processedContent || tagWithId.content

			return {
				...tagWithId,
				content: tagWithId.property === 'og:url' ? normalizeSeoUrl(content) : content,
			}
		})
		registerSeoMetaTags(validMetaTags as any)
	}

	return title
}
